<template>
  <div class="page-layout">
    <!-- Quick Info Header -->
    <div class="quick-info-header" v-if="showHeader">
      <div class="header-left">
        <div class="breadcrumb" v-if="breadcrumb">
          <template v-for="(item, index) in breadcrumb">
            <router-link :key="'link-' + index" :to="item.path" v-if="item.path">
              {{ item.label }}
            </router-link>
            <span :key="'text-' + index" v-else class="current">{{ item.label }}</span>
            <md-icon :key="'icon-' + index" v-if="index < breadcrumb.length - 1">chevron_right</md-icon>
          </template>
        </div>
        <h1 class="page-title">{{ title }}</h1>
        <div class="page-status" v-if="status">
          <span class="status-badge" :class="status.type">
            <span class="status-dot"></span>
            {{ status.label }}
          </span>
          <span class="page-code" v-if="code">{{ code }}</span>
        </div>
      </div>
      <div class="header-right">
        <div class="action-buttons">
          <slot name="actions"></slot>
        </div>
      </div>
    </div>

    <!-- Info Cards -->
    <div class="info-cards" v-if="infoCards && infoCards.length > 0">
      <div class="info-card" v-for="(card, index) in infoCards" :key="index">
        <div class="card-icon" :style="{ background: card.iconBg || '#e8eaf6', color: card.iconColor || '#3949ab' }">
          <md-icon>{{ card.icon }}</md-icon>
        </div>
        <div class="card-content">
          <span class="card-label">{{ card.label }}</span>
          <span class="card-value">{{ card.value }}</span>
        </div>
      </div>
    </div>

    <!-- Tabs or Content -->
    <div class="page-content">
      <slot name="content"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PageLayout',
  props: {
    title: {
      type: String,
      default: ''
    },
    breadcrumb: {
      type: Array,
      default: () => []
    },
    status: {
      type: Object,
      default: null
    },
    code: {
      type: String,
      default: null
    },
    infoCards: {
      type: Array,
      default: () => []
    },
    showHeader: {
      type: Boolean,
      default: true
    }
  }
};
</script>

<style lang="scss" scoped>
.page-layout {
  min-height: 100vh;

  // ============ QUICK INFO HEADER ============
  .quick-info-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    background: white;
    padding: 24px 30px;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    margin-bottom: 20px;

    .header-left {
      .breadcrumb {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        color: #999;

        a {
          color: #667eea;
          text-decoration: none;

          &:hover {
            text-decoration: underline;
          }
        }

        .md-icon {
          font-size: 18px;
          width: 18px;
          height: 18px;
        }

        .current {
          color: #333;
          font-weight: 500;
        }
      }

      .page-title {
        font-size: 26px;
        font-weight: 700;
        color: #2d3748;
        margin: 8px 0;
      }

      .page-status {
        display: flex;
        align-items: center;
        gap: 16px;

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 4px 14px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;

          .status-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            display: inline-block;
          }

          &.active, &.success {
            background: #e8f5e9;
            color: #2e7d32;
            .status-dot { background: #4caf50; }
          }

          &.inactive, &.danger {
            background: #ffebee;
            color: #c62828;
            .status-dot { background: #f44336; }
          }

          &.warning {
            background: #fff3e0;
            color: #e65100;
            .status-dot { background: #ff9800; }
          }

          &.info {
            background: #e3f2fd;
            color: #1565c0;
            .status-dot { background: #2196f3; }
          }

          &.draft {
            background: #f5f5f5;
            color: #616161;
            .status-dot { background: #9e9e9e; }
          }
        }

        .page-code {
          color: #999;
          font-size: 13px;
        }
      }
    }

    .header-right {
      .action-buttons {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;

        .md-button {
          min-width: 100px;

          .md-icon {
            margin-right: 4px;
          }
        }
      }
    }
  }

  // ============ INFO CARDS ============
  .info-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 20px;

    .info-card {
      background: white;
      padding: 16px 20px;
      border-radius: 12px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
      display: flex;
      align-items: center;
      gap: 14px;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
      }

      .card-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 44px;
        border-radius: 10px;
        flex-shrink: 0;

        .md-icon {
          font-size: 22px;
          width: 22px;
          height: 22px;
        }
      }

      .card-content {
        display: flex;
        flex-direction: column;
        min-width: 0;

        .card-label {
          font-size: 11px;
          color: #999;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 600;
        }

        .card-value {
          font-size: 14px;
          font-weight: 600;
          color: #2d3748;
          margin-top: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }

  // ============ PAGE CONTENT ============
  .page-content {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    overflow: hidden;
  }

  // ============ RESPONSIVE ============
  @media (max-width: 768px) {
    .quick-info-header {
      flex-direction: column;
      gap: 16px;
      padding: 16px 20px;

      .header-right {
        width: 100%;

        .action-buttons {
          flex-wrap: wrap;
          width: 100%;

          .md-button {
            flex: 1;
            min-width: 80px;
          }
        }
      }
    }

    .info-cards {
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    }
  }
}
</style>